import { Box, Button, Center, Collapsible, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import type {
  BulkInvitationOutreachEmailResult,
  BulkInvitationOutreachSkipReason,
  BulkInvitationOutreachSkippedItem,
} from '@fluenceur/types';
import { MailOut, NavArrowDown } from 'iconoir-react';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The words a brand reads for each reason an invitation was left out. Keyed by
 * the shared union, so a new backend reason is a compile error here rather than
 * a raw key on screen. A program that is gone reads the same whether it was
 * deleted or never found.
 */
const SKIP_REASON_LABEL_KEY: Record<BulkInvitationOutreachSkipReason, string> = {
  not_found: 'invitations.bulkEmail.skipReasons.not_found',
  undeliverable: 'invitations.bulkEmail.skipReasons.undeliverable',
  already_joined: 'invitations.bulkEmail.skipReasons.already_joined',
  no_email: 'invitations.bulkEmail.skipReasons.no_email',
  program_not_found: 'invitations.bulkEmail.skipReasons.program_deleted',
  program_deleted: 'invitations.bulkEmail.skipReasons.program_deleted',
  program_inactive: 'invitations.bulkEmail.skipReasons.program_inactive',
  recipient_suppressed: 'invitations.bulkEmail.skipReasons.recipient_suppressed',
  recently_emailed: 'invitations.bulkEmail.skipReasons.recently_emailed',
  plan_locked: 'invitations.bulkEmail.skipReasons.plan_locked',
  failed: 'invitations.bulkEmail.skipReasons.failed',
};

interface SkippedGroup {
  labelKey: string;
  count: number;
  /** Invitee names, or their email when the invitation carries no name. */
  names: string[];
}

/** One group per label, in the order the label map declares them. */
const groupSkipped = (skipped: BulkInvitationOutreachSkippedItem[]): SkippedGroup[] => {
  const groups = new Map<string, SkippedGroup>(
    Object.values(SKIP_REASON_LABEL_KEY).map((labelKey) => [
      labelKey,
      { labelKey, count: 0, names: [] },
    ])
  );
  for (const item of skipped) {
    const group = groups.get(SKIP_REASON_LABEL_KEY[item.reason]);
    if (!group) continue;
    group.count += 1;
    const name = item.inviteeName || item.inviteeEmail;
    if (name) group.names.push(name);
  }
  return Array.from(groups.values()).filter((group) => group.count > 0);
};

const GroupHeading: FC<{ group: SkippedGroup }> = ({ group }) => {
  const { t } = useTranslation();
  return (
    <>
      <Text flex="1" minW="0" fontSize="13px" color="dark" lineClamp={1}>
        {t(group.labelKey)}
      </Text>
      <Text fontSize="12px" fontWeight="semibold" color="fg.muted">
        {group.count}
      </Text>
    </>
  );
};

/** A reason with named invitees expands to the names; one without stays a plain row. */
const SkippedGroupRow: FC<{ group: SkippedGroup }> = ({ group }) => {
  if (group.names.length === 0) {
    return (
      <HStack as="li" gap="2" px="12px" py="2">
        <GroupHeading group={group} />
        <Box w="14px" />
      </HStack>
    );
  }

  return (
    <Box as="li">
      <Collapsible.Root lazyMount unmountOnExit>
        <Collapsible.Trigger
          w="full"
          cursor="pointer"
          textAlign="left"
          px="12px"
          py="2"
          _open={{ '& svg': { transform: 'rotate(180deg)' } }}
        >
          <HStack gap="2">
            <GroupHeading group={group} />
            <NavArrowDown width={14} height={14} />
          </HStack>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <VStack as="ul" align="stretch" gap="1" px="12px" pb="2" listStyleType="none">
            {group.names.map((name, index) => (
              <Text as="li" key={`${name}-${index}`} fontSize="12px" color="fg.muted" truncate>
                {name}
              </Text>
            ))}
          </VStack>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
};

interface InvitationBulkOutreachRecapProps {
  result: BulkInvitationOutreachEmailResult;
  onDone: () => void;
}

/**
 * Recap shown once a grouped invitation email returns: how many emails are
 * queued from the brand's mailbox, then the invitations left out, grouped by
 * reason (one line per reason, not per invitation, for up to a hundred). With
 * nothing queued it stays neutral rather than celebrating an empty send.
 */
const InvitationBulkOutreachRecap: FC<InvitationBulkOutreachRecapProps> = ({ result, onDone }) => {
  const { t } = useTranslation();
  const groups = useMemo(() => groupSkipped(result.skipped), [result.skipped]);
  const hasQueued = result.queued > 0;

  return (
    <Flex direction="column" gap="4" align="center" textAlign="center" pt="2" px="1">
      <Center
        data-testid="recap-status-icon"
        data-tone={hasQueued ? 'success' : 'neutral'}
        boxSize="48px"
        rounded="full"
        bg={hasQueued ? 'green.100' : 'bg.muted'}
        color={hasQueued ? 'green.700' : 'fg.muted'}
      >
        <MailOut width={22} height={22} strokeWidth={2.2} />
      </Center>
      <Text fontFamily="heading" fontSize="16px" fontWeight="semibold" color="dark">
        {hasQueued
          ? t('invitations.bulkEmail.recap.queued', { count: result.queued })
          : t('invitations.bulkEmail.recap.noneQueued')}
      </Text>

      {groups.length > 0 ? (
        <Box w="full" textAlign="left">
          <Text fontSize="12.5px" color="fg.muted" mb="1.5">
            {t('invitations.bulkEmail.recap.skipped', { count: result.skipped.length })}
          </Text>
          <Box
            as="ul"
            listStyleType="none"
            borderWidth="1px"
            borderColor="cardBorder"
            rounded="lg"
            bg="panelBg"
            maxH="320px"
            overflowY="auto"
            divideY="1px"
            divideColor="cardBorder"
          >
            {groups.map((group) => (
              <SkippedGroupRow key={group.labelKey} group={group} />
            ))}
          </Box>
        </Box>
      ) : null}

      <Flex justify="center" pt="1" pb="1">
        <Button colorPalette="primary" onClick={onDone}>
          {t('common.close')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default InvitationBulkOutreachRecap;
